import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Rocket, Globe, Plus } from "lucide-react";

export default function Deployments() {
  const [deps, setDeps] = useState([]);
  const [projects, setProjects] = useState([]);
  const [sel, setSel] = useState("");

  const load = () => {
    api.get("/deployments").then((r) => setDeps(r.data)).catch(() => {});
    api.get("/projects").then((r) => setProjects(r.data.filter((p) => p.status === "ready"))).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const deploy = async () => {
    if (!sel) return toast.error("Select a project to deploy");
    try {
      await api.post("/deployments", { project_id: sel, target: "static" });
      toast.success("Deployment configured");
      setSel(""); load();
    } catch (e) { toast.error("Deployment failed"); }
  };

  return (
    <div className="space-y-6" data-testid="deployments-page">
      <div>
        <h1 className="text-2xl font-head font-bold">Deployments</h1>
        <p className="text-slate-400 mt-1">Generate production-ready deployment configuration for a build.</p>
      </div>

      <div className="dc-glass border border-slate-800 rounded-2xl p-5 flex flex-col sm:flex-row gap-3">
        <select data-testid="deploy-project-select" value={sel} onChange={(e) => setSel(e.target.value)}
          className="flex-1 h-11 px-4 rounded-xl bg-slate-950 border border-slate-700 text-sm focus:border-emerald-500 focus:outline-none">
          <option value="">Select a ready project...</option>
          {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <button data-testid="deploy-btn" onClick={deploy}
          className="h-11 px-5 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-500 text-slate-950 font-semibold flex items-center justify-center gap-2">
          <Plus className="h-4 w-4" /> Deploy
        </button>
      </div>

      {deps.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-800 p-16 text-center">
          <Rocket className="h-8 w-8 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-400">No deployments yet.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {deps.map((d) => (
            <div key={d.id} data-testid={`deployment-${d.id}`} className="rounded-2xl border border-slate-800 bg-slate-900/40 p-5 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="h-10 w-10 rounded-xl bg-slate-800/60 flex items-center justify-center"><Rocket className="h-5 w-5 text-indigo-400" /></div>
                <div>
                  <div className="font-head font-semibold">{d.project_name}</div>
                  {d.live_url ? (
                    <a data-testid={`open-live-${d.id}`} href={d.live_url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-sm text-cyan-400 hover:underline"><Globe className="h-3.5 w-3.5" /> {d.domain}</a>
                  ) : (
                    <div className="flex items-center gap-1 text-sm text-slate-400"><Globe className="h-3.5 w-3.5" /> {d.domain}</div>
                  )}
                </div>
              </div>
              <div className="text-right">
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 font-medium">{d.status}</span>
                <div className="text-xs text-slate-500 mt-1">{d.config?.files} files · {d.target}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
